import { EMPTY_ARRAY } from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef,
  type Dispatch,
  type PropsWithChildren,
  type Reducer,
  type SetStateAction,
} from 'react';
import type { SpaceParameterKey, SpaceParameterValues } from './types';

interface SpaceParametersState {
  controllerId: string | undefined;
  values: SpaceParameterValues;
}

type SpaceParametersAction =
  | { type: 'registerController'; id: string; initialValues: SpaceParameterValues }
  | { type: 'unregisterController'; id: string }
  | { type: 'setValues'; values: SetStateAction<SpaceParameterValues> };

function spaceParametersReducer(state: SpaceParametersState, action: SpaceParametersAction): SpaceParametersState {
  switch (action.type) {
    case 'registerController': {
      if (state.controllerId === action.id) {
        return state;
      }
      if (state.controllerId != null) {
        throw new Error(
          `Space parameters already has controller "${state.controllerId}"; cannot register "${action.id}".`
        );
      }
      return {
        ...state,
        controllerId: action.id,
        values: action.initialValues,
      };
    }
    case 'unregisterController': {
      if (state.controllerId !== action.id) {
        return state;
      }
      return {
        ...state,
        controllerId: undefined,
        values: {},
      };
    }
    case 'setValues': {
      const values = action.values instanceof Function ? action.values(state.values) : action.values;
      return isEqual(state.values, values) ? state : { ...state, values };
    }
    default: {
      return state;
    }
  }
}

interface SpaceParametersContextValue {
  /**
   * Whether the space parameters are active, i.e. the controller is registered.
   */
  active: boolean;
  availableKeys: readonly SpaceParameterKey[];
  values: SpaceParameterValues;
  setValues: Dispatch<SetStateAction<SpaceParameterValues>>;
  /**
   * Registers the single panel that controls the space parameters.
   */
  registerController: (id: string, initialValues: SpaceParameterValues) => () => void;
}

const SpaceParametersContext = createContext<SpaceParametersContextValue | undefined>(undefined);
const INITIAL_SPACE_PARAMETERS_STATE: SpaceParametersState = {
  controllerId: undefined,
  values: {},
};

export function SpaceParametersProvider({
  availableKeys = EMPTY_ARRAY,
  children,
}: PropsWithChildren<{ availableKeys?: readonly SpaceParameterKey[] }>) {
  const [state, dispatch] = useReducer<Reducer<SpaceParametersState, SpaceParametersAction>>(
    spaceParametersReducer,
    INITIAL_SPACE_PARAMETERS_STATE
  );
  const controllerRegistrationRef = useRef<{ id: string }>();

  const registerController = useCallback((id: string, initialValues: SpaceParameterValues) => {
    const activeRegistration = controllerRegistrationRef.current;
    if (activeRegistration != null) {
      throw new Error(`Space parameters already has controller "${activeRegistration.id}"; cannot register "${id}".`);
    }
    const registration = { id };
    controllerRegistrationRef.current = registration;
    dispatch({ type: 'registerController', id, initialValues });
    return () => {
      if (controllerRegistrationRef.current !== registration) {
        return;
      }
      controllerRegistrationRef.current = undefined;
      dispatch({ type: 'unregisterController', id });
    };
  }, []);

  const setValues = useCallback<Dispatch<SetStateAction<SpaceParameterValues>>>(values => {
    dispatch({ type: 'setValues', values });
  }, []);

  const value = useMemo<SpaceParametersContextValue>(
    () => ({
      active: state.controllerId != null,
      availableKeys,
      values: state.values,
      setValues,
      registerController,
    }),
    [availableKeys, registerController, setValues, state.controllerId, state.values]
  );

  return <SpaceParametersContext.Provider value={value}>{children}</SpaceParametersContext.Provider>;
}

export function useOptionalSpaceParameters() {
  return useContext(SpaceParametersContext);
}
